/**
 * SoloGameSelectOverlay — the game picker shown at the start of a Solo Grind
 * session (shooting view, no dice). Two cards: Hi-Lo and Chuck-a-Luck. Picking
 * one calls back with the chosen mode, then hides.
 *
 * Single always-mounted overlay on document.body; the `.visible` class is the
 * only state (mirrors the InGameMenu rearchitecture — no display/timer juggling).
 * Closed = transparent + pointer-events:none (click-through). The fade-in
 * commits its hidden base on the first frame (reflow) so Android WebView doesn't
 * flash the visible frame first.
 */

export type SoloSelectMode = 'hilo' | 'chuckaluck';

class SoloGameSelectOverlayImpl {
  private root: HTMLElement | null = null;
  private onSelect: ((mode: SoloSelectMode) => void) | null = null;
  private onCancel: (() => void) | null = null;

  /** Wire the cancel action (leave solo → home). Set once by main.ts. */
  setOnCancel(cb: () => void): void {
    this.onCancel = cb;
  }

  private ensure(): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('div');
    root.className = 'solo-select-overlay';
    root.innerHTML = `
      <button class="solo-select-close" aria-label="Leave solo grind">&times;</button>
      <div class="solo-select-panel">
        <div class="solo-select-title">Choose Your Game</div>
        <div class="solo-select-cards">
          <button class="solo-select-card solo-select-hilo" data-mode="hilo">
            <span class="solo-select-dice">
              <img src="/assets/icons/dice/d-5.svg" alt="">
              <img src="/assets/icons/dice/d-2.svg" alt="">
            </span>
            <span class="solo-select-card-title">HI-LO</span>
            <span class="solo-select-card-sub">Over or under 7 with two dice</span>
            <span class="solo-select-card-odds">
              <span>HI or LO pays <b>1:1</b></span>
              <span>SEVEN pays <b>4:1</b></span>
              <span>A 7 rolls again</span>
            </span>
          </button>
          <button class="solo-select-card solo-select-chuck" data-mode="chuckaluck">
            <span class="solo-select-dice">
              <img src="/assets/icons/dice/d-6.svg" alt="">
              <img src="/assets/icons/dice/d-6.svg" alt="">
              <img src="/assets/icons/dice/d-6.svg" alt="">
            </span>
            <span class="solo-select-card-title">CHUCK-A-LUCK</span>
            <span class="solo-select-card-sub">Pick a number, match three dice</span>
            <span class="solo-select-card-odds">
              <span>1 match pays <b>1:1</b></span>
              <span>2 matches pay <b>2:1</b></span>
              <span>3 matches pay <b>3:1</b></span>
            </span>
          </button>
        </div>
      </div>`;
    document.body.appendChild(root);

    root.querySelectorAll<HTMLElement>('[data-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.mode as SoloSelectMode;
        const cb = this.onSelect;
        this.hide();
        cb?.(mode);
      });
    });

    root.querySelector('.solo-select-close')?.addEventListener('click', () => {
      this.hide();
      this.onCancel?.();
    });

    this.root = root;
  }

  show(onSelect: (mode: SoloSelectMode) => void): void {
    this.ensure();
    this.onSelect = onSelect;
    const root = this.root!;
    // Commit the hidden base (opacity 0) before adding .visible so the fade-in
    // starts from hidden rather than flashing the visible frame.
    void root.offsetHeight;
    root.classList.add('visible');
  }

  hide(): void {
    this.onSelect = null;
    this.root?.classList.remove('visible');
  }

  private injectStyles(): void {
    if (document.getElementById('solo-select-styles')) return;
    const style = document.createElement('style');
    style.id = 'solo-select-styles';
    style.textContent = `
      .solo-select-overlay {
        position: fixed; inset: 0; z-index: 800;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; pointer-events: none;
        transition: opacity 260ms ease;
        background: radial-gradient(ellipse at center, rgba(8,8,10,0.55), rgba(8,8,10,0.82));
      }
      .solo-select-overlay.visible { opacity: 1; pointer-events: auto; }
      .solo-select-close {
        position: absolute; top: calc(env(safe-area-inset-top, 0px) + 12px); right: 14px;
        width: 40px; height: 40px; border-radius: 20px;
        background: rgba(255,255,255,0.08); border: 1.5px solid rgba(255,255,255,0.16);
        color: #C8C8D0; font-size: 26px; line-height: 1; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
      }
      .solo-select-close:active { transform: scale(0.92); }
      .solo-select-panel {
        display: flex; flex-direction: column; align-items: center;
        gap: 20px; padding: 8px 12px; width: min(94vw, 420px);
        transform: translateY(12px) scale(0.98);
        transition: transform 260ms cubic-bezier(0.2, 0.9, 0.3, 1);
      }
      .solo-select-overlay.visible .solo-select-panel { transform: translateY(0) scale(1); }
      .solo-select-title {
        font-family: 'Bebas Neue', Impact, sans-serif;
        font-size: 30px; letter-spacing: 2px; color: #F2F2F5;
        text-shadow: 0 2px 10px rgba(0,0,0,0.6);
      }
      /* Two fluid columns — the cards ALWAYS sit side by side and split the
         panel width evenly (the old fixed 168px cards wrapped to a stack on
         narrow phones by a few px). */
      .solo-select-cards {
        display: flex; gap: 12px; justify-content: center;
        width: 100%; align-items: stretch;
      }
      .solo-select-card {
        display: flex; flex-direction: column; gap: 7px;
        flex: 1 1 0; min-width: 0; max-width: 200px;
        padding: 18px 10px;
        background: linear-gradient(160deg, rgba(28,28,34,0.96), rgba(16,16,20,0.96));
        border: 2px solid rgba(255,255,255,0.12); border-radius: 16px;
        cursor: pointer; color: #F2F2F5; text-align: center;
        transition: transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
      }
      .solo-select-card:active { transform: scale(0.96); }
      .solo-select-hilo { border-color: rgba(0,245,255,0.5); box-shadow: inset 0 14px 24px -18px rgba(0,245,255,0.45); }
      .solo-select-hilo:hover { box-shadow: 0 0 22px rgba(0,245,255,0.35), inset 0 14px 24px -18px rgba(0,245,255,0.45); }
      .solo-select-chuck { border-color: rgba(124,92,255,0.5); box-shadow: inset 0 14px 24px -18px rgba(124,92,255,0.5); }
      .solo-select-chuck:hover { box-shadow: 0 0 22px rgba(124,92,255,0.35), inset 0 14px 24px -18px rgba(124,92,255,0.5); }
      .solo-select-dice {
        display: flex; justify-content: center; gap: 4px;
        margin-bottom: 2px;
      }
      .solo-select-dice img { width: 24px; height: 24px; }
      .solo-select-dice img:first-child { transform: rotate(-9deg); }
      .solo-select-dice img:last-child { transform: rotate(8deg); }
      .solo-select-card-title {
        font-family: 'Bebas Neue', Impact, sans-serif;
        font-size: 23px; letter-spacing: 1.2px; line-height: 1.05;
      }
      .solo-select-hilo .solo-select-card-title { color: #00F5FF; }
      .solo-select-chuck .solo-select-card-title { color: #A78BFF; }
      .solo-select-card-sub { font-size: 11.5px; line-height: 1.35; color: #B8B8C0; }
      .solo-select-card-odds {
        display: flex; flex-direction: column; gap: 3px;
        margin-top: auto; padding-top: 10px;
        border-top: 1px solid rgba(255,255,255,0.08);
        font-size: 10.5px; line-height: 1.3; color: #9A9AA4;
        white-space: nowrap;
      }
      .solo-select-card-odds b { font-weight: 700; }
      .solo-select-hilo .solo-select-card-odds b { color: #00F5FF; }
      .solo-select-chuck .solo-select-card-odds b { color: #A78BFF; }
    `;
    document.head.appendChild(style);
  }
}

/** Always-mounted singleton (created on first show). */
export const soloGameSelect = new SoloGameSelectOverlayImpl();
